import { IResortTerrainDetail, ITerrrainRunsOnly } from 'api/resort/types';
import { TerrainShortSlopeType } from 'components/resort/TerrainShort/constants';
import {
  chooseKeyType, keyType, terrainData, TerrainKeyType,
} from 'components/resort/TerrainShort/data';
import { isDACHRegion, isUSACanada, regionShowKmOnTerrain } from 'i18n/country';
import { slopeType } from 'i18n/preferences';
import { ISlopes } from 'i18n/types';
import { useDistanceFormatter } from 'i18n/unitFormat';
import { useOTSTranslation } from 'i18n/useOTSTranslation';

import { ReactComponent as SvgAdvanced } from '../../../../public/assets/icons/advanced-runs.svg';
import { ReactComponent as SvgBeginners } from '../../../../public/assets/icons/beginner-runs.svg';
import { ReactComponent as SvgCircle } from '../../../../public/assets/icons/circle.svg';
import { ReactComponent as SvgExpert } from '../../../../public/assets/icons/expert-runs.svg';
import { ReactComponent as SvgIntermediate } from '../../../../public/assets/icons/intermediate-runs.svg';

export const useTerrainIconsUniversal = (
  runs: ITerrrainRunsOnly,
  resortCountry: string | undefined,
): IResortTerrainDetail[] | null => {
  const { t } = useOTSTranslation('common');
  const df = useDistanceFormatter('kmOrMile');
  const slope: ISlopes = slopeType(resortCountry);
  const showKilometres = regionShowKmOnTerrain(resortCountry);
  const isDACHReg = isDACHRegion(resortCountry);
  const isUSACan = isUSACanada(resortCountry);
  const { slopesColors } = slope;

  const icons = isUSACan ? {
    beginner: SvgBeginners,
    intermediate: SvgIntermediate,
    advanced: SvgAdvanced,
    expert: SvgExpert,
  } : {
    beginner: SvgCircle,
    intermediate: SvgCircle,
    advanced: SvgCircle,
    expert: SvgCircle,
  };

  const widgetStyle: Record<TerrainShortSlopeType, {
    Icon: React.FC<{ className?: string }>,
    render: boolean,
    className?: string,
    title: string,
    bottom: string,
  }> = {
    beginner: {
      render: !isDACHReg,
      Icon: icons.beginner,
      className: slopesColors.beginner,
      bottom: t('resort.skiReport.openBeginnerRuns'),
      title: t('resort.overview.beginnersRuns'),
    },
    intermediate: {
      render: true,
      Icon: icons.intermediate,
      className: slopesColors.intermediate,
      bottom: t('resort.skiReport.openIntermediateRuns'),
      title: t('resort.overview.intermediateRuns'),
    },
    advanced: {
      render: true,
      Icon: icons.advanced,
      className: slopesColors.advanced,
      bottom: t('resort.skiReport.openAdvancedRuns'),
      title: t('resort.overview.advancedRuns'),
    },
    expert: {
      render: true,
      Icon: icons.expert,
      className: slopesColors.expert,
      bottom: t('resort.skiReport.openExpertRuns'),
      title: t('resort.overview.expertRuns'),
    },
  };

  const dataMap = terrainData(runs, df, t);
  const stat = keyType(dataMap, runs);
  const keyToUse: TerrainKeyType | undefined = chooseKeyType(stat, showKilometres);

  if (!keyToUse) {
    return null; // No open data to show
  }

  const terrainDetails = dataMap.map((item) => ((widgetStyle[item.type].render && item[keyToUse]) ? {
    icon: widgetStyle[item.type].Icon,
    title: widgetStyle[item.type].bottom,
    value: item[keyToUse]!,
    className: widgetStyle[item.type].className,
  }
    : null));

  return terrainDetails.filter((i) => !!i) as IResortTerrainDetail[];
};
